import { Body4 } from '@components/typography/Body4.tsx';
import { Icon } from '@components/Icon.tsx';
import { type IconType } from '@src/IconType.ts';
import { type PropsWithChildren, useMemo } from 'react';
import { classNames } from '@src/utils/classNames';
import EditButton from '@components/EditButton.tsx';
import TrashButton from '@components/TrashButton.tsx';

type ChipProps = Readonly<{
  icon?: IconType;
  label?: string;
  onRemove?: () => void;
  canEdit?: boolean;
  onEdit?: () => void;
  loading?: boolean;
  className?: string;
  testIdKey?: string;
  size?: 'small' | 'medium' | 'large';
}> &
  React.HTMLAttributes<HTMLDivElement>;

export default function Chip({
  icon,
  label,
  onRemove,
  canEdit,
  onEdit,
  loading,
  className,
  testIdKey,
  children,
  size = 'medium',
  ...rest
}: PropsWithChildren<ChipProps>) {
  const sizeClasses = useMemo(() => {
    switch (size) {
      case 'small':
        return 'py-1.5 px-2 rounded-lg';
      case 'medium':
        return 'p-3 rounded-xl';
      case 'large':
        return 'py-3 px-4';
      default:
        return '';
    }
  }, [size]);
  return (
    <div
      className={classNames(
        'ease-out duration-100 group flex flex-row w-fit items-center border-blue-100 border bg-white gap-2.5 hover:bg-gray-100',
        sizeClasses,
        className,
      )}
      {...rest}
    >
      {icon && (
        <Icon
          type={icon}
          className="h-5 w-5 flex-shrink-0"
          aria-hidden="true"
        />
      )}
      {children ? (
        children
      ) : (
        <Body4 className="self-end text-nowrap sm:text-wrap">{label}</Body4>
      )}
      {canEdit && onEdit && (
        <EditButton
          onClick={onEdit}
          loading={loading}
          testId={`edit-button-${testIdKey ?? label}`}
        />
      )}
      {onRemove && (
        <TrashButton
          onClick={onRemove}
          testId={`delete-button-${testIdKey ?? label}`}
        />
      )}
    </div>
  );
}
